import { getPaymentMethods, getPaymentsSummary } from '@/services/wallet';

import type { TGetMethodsFilters } from '@/types/wallet';

import { stableParams, unwrapData } from './utils';

/**
 * Keys of the wallet area (payment methods and the auto-reload summary). Every wallet query starts
 * with `walletKeys.all`.
 * Invalidated by (whole `walletKeys.all`): `useInstantPaymentMethod`, `useCreatePaymentMethod`,
 * `useVerifyPaymentMethod` (`src/mutations/wallet.ts`).
 */
export const walletKeys = {
  all: ['wallet'] as const,
  methods: () => [...walletKeys.all, 'methods'] as const,
  /** `filters` omitted means the service default (ACH methods); it is its own key. */
  method: (filters?: TGetMethodsFilters) =>
    [...walletKeys.methods(), filters ? stableParams(filters) : null] as const,
  paymentsSummary: () => [...walletKeys.all, 'paymentsSummary'] as const,
};

/**
 * Query for the partner's payment methods.
 * What: resolves to the list of payment methods (`response.data.data`).
 * Why: the add-payment-method form, the create-station bank step and the (hidden) station banking
 * section all read the same list; they now share one saved copy per filter.
 * Key: `['wallet', 'methods', filters | null]`. `filters` is `stableParams`-copied, so the
 * `{ id: [...] }` variant is a different key from the default list.
 * Invalidated by: W9 `instantPaymentMethod`, W10 `createPaymentMethod`, W11 `verifyPaymentMethod`.
 *
 * @param filters Optional filters (`paymentMethodType`, `id` list). Omit for the service default.
 */
export const paymentMethodsQuery = (filters?: TGetMethodsFilters) => {
  const stable = filters ? stableParams(filters) : undefined;

  return {
    queryKey: walletKeys.method(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getPaymentMethods(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the auto-reload summary shown by the (currently hidden) Dashboard Payments card.
 * What: resolves to `{ amount, depositedDate?, nextReloadDate? }` (`response.data.data`).
 * Key: `['wallet', 'paymentsSummary']`.
 * Invalidated by: any wallet save (they invalidate the whole `walletKeys.all`).
 */
export const paymentsSummaryQuery = () => ({
  queryKey: walletKeys.paymentsSummary(),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getPaymentsSummary(signal).then((response) => unwrapData(response.data.data)),
});
